import React, { useMemo, useState } from "react";

import ReportHeader from "../components/reports/header/ReportHeader";
import KPICard from "../components/reports/shared/KPICard";
import KPICardGrid from "../components/reports/shared/KPICardGrid";
import CustomersTab from "../components/reports/tabs/CustomersTab";
import InvoicesTab from "../components/reports/tabs/InvoicesTab";
import OverviewTab from "../components/reports/tabs/OverviewTab";
import RevenueTab from "../components/reports/tabs/RevenueTab";
import SupportTab from "../components/reports/tabs/SupportTab";
import { useExport } from "../hooks/useExport";
import { useReportsData } from "../hooks/useReportsData";
import useRouteTab from "../hooks/useRouteTab";
import { formatCurrency } from "../utils/reportHelpers";

const TABS = [
  { id: "overview", label: "Overview", icon: "fas fa-chart-line" },
  { id: "revenue", label: "Revenue", icon: "fas fa-dollar-sign" },
  { id: "customers", label: "Customers", icon: "fas fa-users" },
  { id: "support", label: "Support", icon: "fas fa-headset" },
  { id: "invoices", label: "Invoices", icon: "fas fa-file-invoice" },
];
const TAB_IDS = TABS.map((tab) => tab.id);

const Reports = () => {
  const [dateRange, setDateRange] = useState({
    type: "preset",
    preset: "30days",
    start: "",
    end: "",
  });
  const [comparison, setComparison] = useState("previousPeriod");
  const [activeTab, setActiveTab] = useRouteTab({
    defaultTab: "overview",
    validTabs: TAB_IDS,
  });

  const data = useReportsData(dateRange, comparison);
  const { exportCSV, exportPDF, exportFullPDF } = useExport(
    data,
    dateRange,
    activeTab
  );

  const showComparison = comparison !== "none";

  // Top-level KPI stats — memoised to prevent child re-renders
  const stats = useMemo(
    () => [
      {
        label: "Total Revenue",
        value: data.loading ? "..." : formatCurrency(data.revenue.lifetime),
        delta: data.comparison.revenueDelta.value,
        deltaLabel: "vs previous period",
        icon: "fas fa-dollar-sign",
        color: "bg-green-500",
      },
      {
        label: "Active Customers",
        value: data.loading ? "..." : data.customers.total.toLocaleString(),
        delta: data.comparison.customerDelta.value,
        deltaLabel: "vs previous period",
        icon: "fas fa-users",
        color: "bg-blue-500",
      },
      {
        label: "Support Tickets",
        value: data.loading ? "..." : String(data.tickets.total),
        delta: data.comparison.ticketDelta.value,
        deltaLabel: "vs previous period",
        icon: "fas fa-ticket-alt",
        color: "bg-purple-500",
      },
      {
        label: "Avg Response Time",
        value: data.loading ? "..." : data.tickets.avgResolutionTime,
        delta: data.comparison.responseDelta.value,
        deltaLabel: "vs previous period",
        icon: "fas fa-clock",
        color: "bg-orange-500",
      },
    ],
    [data.loading, data.revenue, data.customers, data.tickets, data.comparison]
  );

  const handleExport = (type) => {
    if (type === "csv") exportCSV();
    else if (type === "pdf") exportPDF();
    else if (type === "fullPdf") exportFullPDF();
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Header */}
      <ReportHeader
        comparison={comparison}
        dateRange={dateRange}
        refreshing={data.loading}
        onComparisonChange={setComparison}
        onDateRangeChange={setDateRange}
        onExport={handleExport}
        onRefresh={data.refetch}
      />

      {/* KPI Stats Cards */}
      <KPICardGrid columns={4}>
        {stats.map((stat, index) => (
          <KPICard
            index={index}
            key={stat.label}
            loading={data.loading}
            {...stat}
          />
        ))}
      </KPICardGrid>

      {/* Tabs */}
      <div className="card shadow-md">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex overflow-x-auto scrollbar-hide px-3 sm:px-6">
            {TABS.map((tab) => (
              <button
                className={`py-3 sm:py-4 px-2 sm:px-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600 dark:text-primary-400"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`${tab.icon} mr-1 sm:mr-2`} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === "overview" && (
            <OverviewTab
              data={data}
              loading={data.loading}
              showComparison={showComparison}
            />
          )}
          {activeTab === "revenue" && (
            <RevenueTab
              data={data}
              loading={data.loading}
              showComparison={showComparison}
            />
          )}
          {activeTab === "customers" && (
            <CustomersTab data={data} loading={data.loading} />
          )}
          {activeTab === "support" && (
            <SupportTab data={data} loading={data.loading} />
          )}
          {activeTab === "invoices" && (
            <InvoicesTab data={data} loading={data.loading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
